import { useState } from "react";
import { ChevronLeft, ChevronRight, Filter } from "lucide-react";
import { Button, FormField, SelectInput } from "../../components/ui";
import {
  ATALHOS_DE_PERIODO,
  NOMES_DOS_MESES,
  calcularPeriodoPorAtalho,
  deslocarCompetencia,
  formatarPeriodo,
  getCompetenciaAtualLocal,
  montarCompetencia,
  periodoEhValido,
  periodoTemUmSoMes,
  separarCompetencia,
} from "./dashboardDespesaUtils";

const ATALHOS = [
  { chave: ATALHOS_DE_PERIODO.MES_ATUAL, rotulo: "Mês atual" },
  { chave: ATALHOS_DE_PERIODO.ULTIMOS_3_MESES, rotulo: "Últimos 3 meses" },
  { chave: ATALHOS_DE_PERIODO.ULTIMOS_6_MESES, rotulo: "Últimos 6 meses" },
  { chave: ATALHOS_DE_PERIODO.ANO_ATUAL, rotulo: "Ano atual" },
];

const ANOS_ANTES_DO_ATUAL = 5;
const ANOS_DEPOIS_DO_ATUAL = 1;

const gerarOpcoesDeAno = () => {
  const anoAtual = new Date().getFullYear();
  const anos = [];

  for (
    let ano = anoAtual - ANOS_ANTES_DO_ATUAL;
    ano <= anoAtual + ANOS_DEPOIS_DO_ATUAL;
    ano += 1
  ) {
    anos.push(ano);
  }

  return anos;
};

const SeletorCompetencia = ({ idBase, rotulo, competencia, onChange }) => {
  const { ano, mes } = separarCompetencia(competencia);

  return (
    <div className="grid grid-cols-2 gap-2">
      <FormField label={`${rotulo} - mês`} htmlFor={`${idBase}-mes`}>
        <SelectInput
          id={`${idBase}-mes`}
          value={mes}
          onChange={(e) =>
            onChange(montarCompetencia(ano, Number(e.target.value)))
          }
        >
          {NOMES_DOS_MESES.map((nome, indice) => (
            <option key={nome} value={indice + 1}>
              {nome}
            </option>
          ))}
        </SelectInput>
      </FormField>

      <FormField label={`${rotulo} - ano`} htmlFor={`${idBase}-ano`}>
        <SelectInput
          id={`${idBase}-ano`}
          value={ano}
          onChange={(e) =>
            onChange(montarCompetencia(Number(e.target.value), mes))
          }
        >
          {gerarOpcoesDeAno().map((opcao) => (
            <option key={opcao} value={opcao}>
              {opcao}
            </option>
          ))}
        </SelectInput>
      </FormField>
    </div>
  );
};

// Mostra o período aplicado, navega mês a mês e abre o painel de filtros
// (atalhos e período personalizado). A consulta só muda quando o período
// aplicado muda; o rascunho do painel não dispara nada.
const SeletorPeriodoDashboard = ({
  periodo,
  carregando,
  onAplicar,
  onErro,
}) => {
  const [painelAberto, setPainelAberto] = useState(false);
  const [rascunho, setRascunho] = useState(periodo);

  const umSoMes = periodoTemUmSoMes(periodo.inicio, periodo.fim);
  const setasDesativadas = carregando || !umSoMes;

  const navegar = (deslocamento) => {
    const novaCompetencia = deslocarCompetencia(periodo.inicio, deslocamento);
    onAplicar({ inicio: novaCompetencia, fim: novaCompetencia });
  };

  const abrirOuFecharPainel = () => {
    if (!painelAberto) {
      setRascunho(periodo);
    }
    setPainelAberto((aberto) => !aberto);
  };

  const aplicarAtalho = (atalho) => {
    const novoPeriodo = calcularPeriodoPorAtalho(atalho);
    setRascunho(novoPeriodo);
    onAplicar(novoPeriodo);
    setPainelAberto(false);
  };

  const aplicarRascunho = () => {
    if (!periodoEhValido(rascunho.inicio, rascunho.fim)) {
      onErro("A competência inicial não pode ser posterior à final");
      return;
    }

    onAplicar(rascunho);
    setPainelAberto(false);
  };

  const cancelar = () => {
    setRascunho(periodo);
    setPainelAberto(false);
  };

  const voltarAoMesAtual = () => {
    const atual = getCompetenciaAtualLocal();
    const mesAtual = { inicio: atual, fim: atual };
    setRascunho(mesAtual);
    onAplicar(mesAtual);
    setPainelAberto(false);
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => navegar(-1)}
          disabled={setasDesativadas}
          aria-label="Mês anterior"
          className="p-2 rounded-md border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <ChevronLeft size={16} />
        </button>

        <div
          className="min-w-[12rem] text-center font-semibold text-gray-900"
          aria-live="polite"
        >
          {formatarPeriodo(periodo.inicio, periodo.fim)}
        </div>

        <button
          type="button"
          onClick={() => navegar(1)}
          disabled={setasDesativadas}
          aria-label="Próximo mês"
          className="p-2 rounded-md border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <ChevronRight size={16} />
        </button>

        <Button
          variant="secondary"
          icon={Filter}
          onClick={abrirOuFecharPainel}
          aria-expanded={painelAberto}
        >
          Filtros
        </Button>
      </div>

      {painelAberto && (
        <div className="p-4 border border-gray-200 rounded-lg bg-gray-50 space-y-4">
          <div className="flex flex-wrap gap-2">
            {ATALHOS.map((atalho) => (
              <Button
                key={atalho.chave}
                variant="secondary"
                size="sm"
                onClick={() => aplicarAtalho(atalho.chave)}
                disabled={carregando}
              >
                {atalho.rotulo}
              </Button>
            ))}
          </div>

          <div>
            <p className="text-sm font-semibold text-gray-900 mb-2">
              Período personalizado
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <SeletorCompetencia
                idBase="dashboard-periodo-inicio"
                rotulo="Início"
                competencia={rascunho.inicio}
                onChange={(inicio) =>
                  setRascunho((prev) => ({ ...prev, inicio }))
                }
              />
              <SeletorCompetencia
                idBase="dashboard-periodo-fim"
                rotulo="Fim"
                competencia={rascunho.fim}
                onChange={(fim) => setRascunho((prev) => ({ ...prev, fim }))}
              />
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button onClick={aplicarRascunho} disabled={carregando}>
              Aplicar
            </Button>
            <Button variant="secondary" onClick={cancelar}>
              Cancelar
            </Button>
            <Button
              variant="secondary"
              onClick={voltarAoMesAtual}
              disabled={carregando}
            >
              Voltar ao mês atual
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SeletorPeriodoDashboard;
